import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/authContext.ts";
import { HipotesisInline } from "../../components/HipotesisNormativas.tsx";
import {
  actualizarCompraventa,
  agregarContraparte,
  agregarCopropietario,
  agregarInmuebleAdicional,
  apiConfigurada,
  cobrosDeCompraventa,
  crearAportacion,
  crearCompraventa,
  eliminarContraparte,
  eliminarCopropietario,
  eliminarInmuebleAdicional,
  evaluacionesUltimas,
  hipotesisPorAportaciones,
  listarCatalogo,
  listarCompraventas,
  listarContrapartes,
  listarCopropietarios,
  listarInmueblesAdicionales,
  listarPersonas,
  listarProyectos,
  type AportacionRow,
  type CatalogoRow,
  type CompraventaInmuebleRow,
  type CompraventaRow,
  type ContraparteRow,
  type CopropietarioRow,
  type EtapaCompraventa,
  type EvaluacionMotor,
  type HipotesisNormativa,
  type PersonaRow,
  type ProyectoMin,
} from "../../lib/api.ts";
import { C, st, dinero, fecha } from "../../ui/theme.ts";
import { Chip, Fila } from "../../ui/componentes.tsx";
import { IconoEdificio } from "../../ui/iconos.tsx";
import { nombrePersona } from "../../ui/persona.ts";
import { faltantesDatosInm } from "./validacionesRec.ts";

/**
 * COMPRAVENTAS — rebanada 3 de la beta. La venta de unidades propias (fr. V,
 * 0023): contenedor comercial con etapa apartado→contrato→escritura, y COBROS
 * anidados que son aportaciones evaluadas por el motor real. El formato oficial
 * SAT de Inmuebles existe; Argus genera el XML y conserva el ciclo probatorio
 * generar -> validar -> presentar -> acuse por separado del DIN.
 */

const OPACIDAD_DESHABILITADO = 0.6;
const CLAVE_EFECTIVO = "1";
const CIEN = 100;
const LONGITUD_ENTIDAD_FEDERATIVA = 2;
const LONGITUD_FOLIO_REAL = 200;
const LONGITUD_FOLIO_INSTRUMENTO = 20;
const LONGITUD_NOTARIO = 8;

const ETAPAS: [EtapaCompraventa, string][] = [
  ["apartado", "Apartado"],
  ["contrato_privado", "Contrato privado"],
  ["escriturada", "Escriturada"],
];

const INSTRUMENTOS: [string, string][] = [
  ["1", "Efectivo"],
  ["2", "Tarjeta de Crédito"],
  ["3", "Tarjeta de Debito"],
  ["4", "Tarjeta de Prepago"],
  ["5", "Cheque Nominativo"],
  ["6", "Cheque de Caja"],
  ["7", "Cheques de Viajero"],
  ["8", "Transferencia Interbancaria"],
  ["9", "Transferencia Misma Institución"],
  ["10", "Transferencia Internacional"],
  ["11", "Orden de Pago"],
  ["12", "Giro"],
  ["13", "Oro o Platino Amonedados"],
  ["14", "Plata Amonedada"],
  ["15", "Metales Preciosos"],
  ["16", "Activos Virtuales"],
  ["99", "Otros"],
];

const FORMAS_PAGO_INM: [string, string][] = [
  ["1", "Contado"],
  ["2", "Diferido o en parcialidades"],
  ["3", "Dación en pago"],
  ["4", "Préstamo o crédito"],
  ["5", "Permuta"],
];

const TIPOS_OPERACION_INM: [string, string][] = [
  ["501", "Compraventa de inmuebles"],
];

const FIGURAS_CLIENTE_INM: [string, string][] = [
  ["1", "Vendedor"],
  ["2", "Comprador"],
];

const FIGURAS_SO_INM: [string, string][] = [
  ["1", "Vendedor"],
  ["2", "Comprador"],
];

const TIPOS_INMUEBLE: [string, string][] = [
  ["1", "Casa /Casa en condominio"],
  ["2", "Departamento"],
  ["3", "Edificio habitacional"],
  ["4", "Edificio comercial"],
  ["5", "Edificio oficinas"],
  ["6", "Local comercial independiente"],
  ["7", "Local en centro comercial"],
  ["8", "Oficina"],
  ["9", "Bodega comercial"],
  ["10", "Bodega industrial"],
  ["11", "Nave Industrial"],
  ["12", "Terreno urbano habitacional"],
  ["13", "Terreno no urbano habitacional"],
  ["14", "Terreno urbano comercial o industrial"],
  ["15", "Terreno no urbano comercial o industrial"],
  ["16", "Terreno ejidal"],
  ["17", "Rancho/Hacienda/Quinta"],
  ["18", "Huerta"],
  ["99", "Otro"],
];

const ENTIDADES_INM: [string, string][] = [
  ["01", "AGUASCALIENTES"],
  ["02", "BAJA CALIFORNIA"],
  ["03", "BAJA CALIFORNIA SUR"],
  ["04", "CAMPECHE"],
  ["05", "COAHUILA DE ZARAGOZA"],
  ["06", "COLIMA"],
  ["07", "CHIAPAS"],
  ["08", "CHIHUAHUA"],
  ["09", "DISTRITO FEDERAL"],
  ["10", "DURANGO"],
  ["11", "GUANAJUATO"],
  ["12", "GUERRERO"],
  ["13", "HIDALGO"],
  ["14", "JALISCO"],
  ["15", "MEXICO"],
  ["16", "MICHOACAN DE OCAMPO"],
  ["17", "MORELOS"],
  ["18", "NAYARIT"],
  ["19", "NUEVO LEON"],
  ["20", "OAXACA"],
  ["21", "PUEBLA"],
  ["22", "QUERETARO"],
  ["23", "QUINTANA ROO"],
  ["24", "SAN LUIS POTOSI"],
  ["25", "SINALOA"],
  ["26", "SONORA"],
  ["27", "TABASCO"],
  ["28", "TAMAULIPAS"],
  ["29", "TLAXCALA"],
  ["30", "VERACRUZ DE IGNACIO DE LA LLAVE"],
  ["31", "YUCATAN"],
  ["32", "ZACATECAS"],
];

const MONEDAS_INM: [string, string][] = [
  ["1", "Peso mexicano"],
  ["2", "Dólar estadounidense"],
  ["3", "Euro"],
];

type CatalogoOption = [string, string];

interface CatalogosInm {
  instrumentos: CatalogoOption[];
  monedas: CatalogoOption[];
  tiposInmueble: CatalogoOption[];
  formasPago: CatalogoOption[];
  figurasCliente: CatalogoOption[];
  figurasSo: CatalogoOption[];
  entidades: CatalogoOption[];
}

const CATALOGOS_INM_FALLBACK: CatalogosInm = {
  instrumentos: INSTRUMENTOS,
  monedas: MONEDAS_INM,
  tiposInmueble: TIPOS_INMUEBLE,
  formasPago: FORMAS_PAGO_INM,
  figurasCliente: FIGURAS_CLIENTE_INM,
  figurasSo: FIGURAS_SO_INM,
  entidades: ENTIDADES_INM,
};

function opcionesCatalogo(rows: CatalogoRow[]): CatalogoOption[] {
  return rows.map((r) => [r.clave, r.nombre]);
}

function numeroONull(v: string): number | null {
  const n = Number(v);
  return Number.isFinite(n) && v.trim() !== "" ? n : null;
}

function textoONull(v: string): string | null {
  const limpio = v.trim();
  return limpio === "" ? null : limpio;
}

function claveXml(v: string, max: number): string {
  return v.toUpperCase().slice(0, max);
}

function entidadONull(v: string): string | null {
  const limpio = v.trim();
  return limpio === "" ? null : limpio.padStart(LONGITUD_ENTIDAD_FEDERATIVA, "0");
}

function figuraSoSoportada(v: string | null | undefined): string {
  const claveValor = v ?? "";
  return FIGURAS_SO_INM.some(([clave]) => clave === claveValor) ? claveValor : "2";
}

/** Nombre para desplegar de una PersonaRow (PF/PM/fideicomiso). */
function nombrePersonaRow(p: PersonaRow): string {
  if (p.tipo === "pm") return p.denominacion_razon ?? "(sin razón social)";
  if (p.tipo === "fideicomiso") {
    return p.fideicomiso_identificador ? `Fideicomiso ${p.fideicomiso_identificador}` : "(fideicomiso)";
  }
  return [p.nombre, p.apellido_paterno, p.apellido_materno].filter((x): x is string => !!x).join(" ") || "(sin nombre)";
}

function etiquetaCatalogo(cat: [string, string][], clave: string | null | undefined, fallback: string): string {
  if (!clave) return fallback;
  return cat.find(([k]) => k === clave)?.[1] ?? `${fallback} ${clave}`;
}

function chipEtapa(etapa: string) {
  if (etapa === "escriturada") return <Chip tono="verde">Escriturada</Chip>;
  if (etapa === "contrato_privado") return <Chip tono="azul">Contrato privado</Chip>;
  return <Chip tono="gris">Apartado</Chip>;
}

/** Veredicto del motor para un cobro fr. V, en español. */
function veredictoCobro(e: EvaluacionMotor | undefined): { tono: "verde" | "ambar" | "rojo" | "gris"; texto: string } | null {
  if (!e) return null;
  if (e.prohibicion_efectivo) {
    return { tono: "rojo", texto: "Efectivo sobre el límite legal (Art. 32) — el cobro no puede aplicarse; queda constancia." };
  }
  if (e.prohibicion_indeterminada) {
    return { tono: "gris", texto: "Límite de efectivo indeterminado a la fecha — revisión del REC." };
  }
  if (e.requiere_aviso) {
    if (e.adaptador_pendiente) {
      const formato = e.formato_oficial ? ` (${e.formato_oficial})` : "";
      return {
        tono: "ambar",
        texto: `Cruza el umbral: el formato oficial SAT de Inmuebles existe${formato}, pero Argus aún no genera ese XML. La obligación quedó registrada con reloj de 17 días hábiles.`,
      };
    }
    if (e.aviso_sin_formato) {
      return {
        tono: "ambar",
        texto: "Cruza el umbral: la obligación de aviso quedó REGISTRADA con su reloj de 17 días hábiles. El SAT aún no publica formato oficial para esta fracción.",
      };
    }
    if (e.formato_aviso === "INMUEBLES_XML") {
      return { tono: "ambar", texto: "Cruza el umbral: genera exportación XML de Inmuebles y conserva ciclo de validación, presentación y acuse separado del DIN." };
    }
    return { tono: "ambar", texto: "Cruza el umbral: genera aviso ligado al informe del mes." };
  }
  return { tono: "verde", texto: "Sin obligación de aviso; suma en la ventana de 6 meses del comprador (fr. V, aparte de captación)." };
}

export function CompraventasView() {
  const { session } = useAuth();
  const token = session?.accessToken ?? "";
  const tenantId = session?.tenantId ?? "";
  const demo = !apiConfigurada() || token === "";

  const [cvs, setCvs] = useState<CompraventaRow[]>([]);
  const [personas, setPersonas] = useState<PersonaRow[]>([]);
  const [proyectos, setProyectos] = useState<ProyectoMin[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [modo, setModo] = useState<"detalle" | "nueva">("detalle");
  const [cobros, setCobros] = useState<AportacionRow[] | null>(null);
  const [evals, setEvals] = useState<Map<string, EvaluacionMotor>>(new Map());
  const [hipotesis, setHipotesis] = useState<Map<string, HipotesisNormativa[]>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(!demo);
  const [ocupado, setOcupado] = useState(false);
  const [catalogos, setCatalogos] = useState<CatalogosInm>(CATALOGOS_INM_FALLBACK);

  const sel = cvs.find((c) => c.id === selId) ?? null;

  const recargar = useCallback(async () => {
    if (demo) return;
    const [xs, per, pro] = await Promise.all([listarCompraventas(token), listarPersonas(token), listarProyectos(token)]);
    setCvs(xs);
    setPersonas(per);
    setProyectos(pro);
  }, [demo, token]);

  const cargarCobros = useCallback(async (cvId: string) => {
    if (demo) {
      setCobros([]);
      return;
    }
    setCobros(null);
    const filas = await cobrosDeCompraventa(token, cvId);
    setCobros(filas);
    const ids = filas.map((f) => f.id);
    const [ev, hn] = await Promise.all([evaluacionesUltimas(token, ids), hipotesisPorAportaciones(token, ids)]);
    setEvals(ev);
    setHipotesis(hn);
  }, [demo, token]);

  useEffect(() => {
    if (demo) return;
    setCargando(true);
    recargar()
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudieron cargar las compraventas."); })
      .finally(() => { setCargando(false); });
  }, [demo, recargar]);

  useEffect(() => {
    if (demo) {
      setCatalogos(CATALOGOS_INM_FALLBACK);
      return;
    }
    Promise.all([
      listarCatalogo(token, "cat_instrumento_monetario"),
      listarCatalogo(token, "cat_moneda"),
      listarCatalogo(token, "cat_inm_tipo_inmueble"),
      listarCatalogo(token, "cat_inm_forma_pago"),
      listarCatalogo(token, "cat_inm_figura_cliente"),
      listarCatalogo(token, "cat_inm_figura_so"),
      listarCatalogo(token, "cat_inm_entidad_federativa"),
    ])
      .then(([instrumentos, monedas, tiposInmueble, formasPago, figurasCliente, figurasSo, entidades]) => {
        setCatalogos({
          instrumentos: opcionesCatalogo(instrumentos),
          monedas: opcionesCatalogo(monedas),
          tiposInmueble: opcionesCatalogo(tiposInmueble),
          formasPago: opcionesCatalogo(formasPago),
          figurasCliente: opcionesCatalogo(figurasCliente),
          figurasSo: opcionesCatalogo(figurasSo.filter((r) => r.soportado !== false)),
          entidades: opcionesCatalogo(entidades),
        });
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "No se pudieron cargar los catálogos oficiales INM.");
      });
  }, [demo, token]);

  useEffect(() => {
    if (sel && modo === "detalle") {
      cargarCobros(sel.id).catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "No se pudieron cargar los cobros.");
      });
    }
  }, [sel, modo, cargarCobros]);

  const cambiarEtapa = async (etapa: EtapaCompraventa) => {
    if (!sel) return;
    setError(null);
    setOcupado(true);
    try {
      if (demo) {
        setCvs((xs) => xs.map((c) => (c.id === sel.id ? { ...c, etapa } : c)));
      } else {
        await actualizarCompraventa(token, sel.id, { etapa });
        await recargar();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo cambiar la etapa.");
    } finally {
      setOcupado(false);
    }
  };

  const total = (cobros ?? []).reduce((s, c) => s + (Number(c.monto_bruto) || 0), 0);

  return (
    <div className="argus-master-detail" style={{ display: "flex", flex: 1, minWidth: 0 }}>
      {/* Lista central */}
      <section className="argus-list-pane" style={{ width: 330, flexShrink: 0, borderRight: `1px solid ${C.line}`, display: "flex", flexDirection: "column", background: "#FBFCFB" }}>
        <div style={{ padding: "16px 16px 10px" }}>
          <h2 style={{ ...st.display, margin: 0, fontSize: 18 }}>Compraventas</h2>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>
            Venta de unidades propias (fr. V). Los cobros los evalúa el motor, aparte de captación.
          </p>
        </div>
        {demo && (
          <div style={{ margin: "8px 16px", padding: "6px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 11.5, fontWeight: 600 }}>
            Modo demostración — conecta el backend para operar con el motor real.
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto", padding: "6px 12px 8px" }}>
          {cargando && <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Cargando…</p>}
          {!cargando && cvs.length === 0 && (
            <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Sin compraventas — registra la primera.</p>
          )}
          {cvs.map((cv) => {
            const activa = selId === cv.id && modo === "detalle";
            return (
              <button
                key={cv.id}
                onClick={() => { setSelId(cv.id); setModo("detalle"); setError(null); }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "#fff",
                  border: `1px solid ${activa ? C.tiel : C.line}`,
                  boxShadow: activa ? `0 0 0 1px ${C.tiel}` : "none",
                  borderRadius: 12,
                  padding: "10px 12px",
                  marginBottom: 8,
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13.5, color: C.ink }}>{cv.unidad}</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: C.ink, whiteSpace: "nowrap" }}>{dinero(cv.precio_pactado)}</span>
                </div>
                <div style={{ fontSize: 12, color: C.muted, margin: "2px 0 6px" }}>
                  {nombrePersona(cv.cliente_aportante)} · {cv.proyecto?.nombre ?? "—"}
                </div>
                {chipEtapa(cv.etapa)}
              </button>
            );
          })}
        </div>
        <div style={{ padding: 12, borderTop: `1px solid ${C.line}` }}>
          <button style={{ ...st.btnPrim, width: "100%" }} onClick={() => { setModo("nueva"); setSelId(null); setError(null); }}>
            ＋ Registrar compraventa
          </button>
        </div>
      </section>

      {/* Detalle / captura */}
      <main className="argus-detail-pane" style={{ flex: 1, overflow: "auto", padding: "22px 26px", minWidth: 0 }}>
        {error && (
          <div style={{ maxWidth: 560, margin: "0 auto 12px", padding: "10px 14px", borderRadius: 10, background: C.redBg, color: C.red, fontSize: 13 }}>
            {error}
          </div>
        )}
        {modo === "nueva" ? (
          <FormCompraventa
            demo={demo}
            catalogos={catalogos}
            personas={personas}
            proyectos={proyectos}
            onCancelar={() => { setModo("detalle"); }}
            onCrear={async (datos) => {
              setError(null);
              try {
                if (demo) {
                  setError("En modo demostración el alta requiere backend conectado.");
                  return;
                }
                const fila = await crearCompraventa(token, tenantId, datos);
                await recargar();
                setSelId(fila.id);
                setModo("detalle");
              } catch (e) {
                setError(e instanceof Error ? e.message : "No se pudo registrar la compraventa.");
              }
            }}
          />
        ) : sel ? (
          <div style={{ maxWidth: 560, margin: "0 auto" }}>
            <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>{sel.unidad}</h2>
            <p style={{ margin: "0 0 10px", color: C.muted, fontSize: 13 }}>
              {nombrePersona(sel.cliente_aportante)} · {sel.proyecto?.nombre ?? "—"} · {fecha(sel.fecha_operacion)}
            </p>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
              {chipEtapa(sel.etapa)}
              <Chip tono="tiel">fr. V · venta de unidad propia</Chip>
              {sel.cliente_aportante && !sel.cliente_aportante.expediente_completo && (
                <Chip tono="ambar">Expediente del comprador incompleto</Chip>
              )}
            </div>

            <div style={st.tarjeta}>
              <p style={st.eyebrow}>Operación</p>
              <Fila k="Precio pactado (sin IVA)" v={dinero(sel.precio_pactado)} />
              <Fila k="Cobrado a la fecha" v={`${dinero(total)}${Number(sel.precio_pactado) > 0 ? ` (${String(Math.round((total / Number(sel.precio_pactado)) * CIEN))}%)` : ""}`} />
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, color: C.muted }}>Etapa:</span>
                {ETAPAS.map(([v, t]) => (
                  <button
                    key={v}
                    disabled={ocupado || sel.etapa === v}
                    onClick={() => { void cambiarEtapa(v); }}
                    style={{
                      ...st.btn,
                      padding: "6px 11px",
                      fontSize: 12,
                      borderColor: sel.etapa === v ? C.tiel : C.line,
                      background: sel.etapa === v ? C.tielSoft : "#fff",
                      color: sel.etapa === v ? C.tielDark : C.ink,
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <DatosInmueblesCard
              compraventa={sel}
              catalogos={catalogos}
              ocupado={ocupado}
              onGuardar={async (patch) => {
                setError(null);
                setOcupado(true);
                try {
                  if (demo) {
                    setError("En modo demostración la edición requiere backend conectado.");
                    return;
                  }
                  await actualizarCompraventa(token, sel.id, patch);
                  await recargar();
                } catch (e) {
                  setError(e instanceof Error ? e.message : "No se pudieron guardar los datos INM.");
                } finally {
                  setOcupado(false);
                }
              }}
            />

            <CopropietariosCard
              demo={demo}
              token={token}
              tenantId={tenantId}
              compraventaId={sel.id}
              personas={personas}
              principalId={sel.cliente_aportante?.id ?? null}
            />

            <InmueblesAdicionalesCard
              demo={demo}
              token={token}
              tenantId={tenantId}
              compraventaId={sel.id}
              catalogos={catalogos}
            />

            <ContraparteCard
              demo={demo}
              token={token}
              tenantId={tenantId}
              compraventaId={sel.id}
              personas={personas}
              figuraSo={sel.figura_so}
            />

            <CobrosCard
              demo={demo}
              catalogos={catalogos}
              cobros={cobros}
              evals={evals}
              hipotesis={hipotesis}
              onRegistrar={async (datos) => {
                setError(null);
                setOcupado(true);
                try {
                  if (demo) {
                    throw new Error("En modo demostración el cobro requiere backend conectado.");
                  }
                  await crearAportacion(token, tenantId, {
                    ...datos,
                    compraventa_id: sel.id,
                    proyecto_id: sel.proyecto?.id,
                    cliente_aportante_id: sel.cliente_aportante?.id,
                  });
                  await cargarCobros(sel.id);
                } catch (e) {
                  setError(e instanceof Error ? e.message : "No se pudo registrar el cobro.");
                  throw e;
                } finally {
                  setOcupado(false);
                }
              }}
            />

            <div style={{ ...st.tarjeta, background: C.tielSoft, borderColor: "#C8E0DD" }}>
              <p style={{ ...st.eyebrow, color: C.tielDark }}>Cómo funciona</p>
              <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.7 }}>
                Cada cobro se evalúa igual que la captación (doble base, acumulación de 6 meses
                del comprador, límite de efectivo). Ventas y captación suman POR SEPARADO — son
                dos riesgos distintos. El aviso de fr. V queda registrado con su reloj; Argus
                genera el XML oficial de Inmuebles y conserva el ciclo de validación, presentación
                y acuse separado del DIN.
              </div>
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: 420, margin: "18vh auto 0", textAlign: "center", color: C.muted }}>
            <div style={{ marginBottom: 8, color: C.tielDark }}><IconoEdificio size={34} /></div>
            <h3 style={{ margin: "0 0 6px", color: C.ink }}>Selecciona una compraventa</h3>
            <p style={{ fontSize: 13.5, margin: 0 }}>
              La unidad, su comprador y sus cobros — cada cobro evaluado por el motor, con el
              límite de efectivo vigilado con IVA incluido.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Datos INM de la unidad/instrumento jurídico
// ─────────────────────────────────────────────────────────────────────────────

function DatosInmueblesCard({
  compraventa,
  catalogos,
  ocupado,
  onGuardar,
}: {
  compraventa: CompraventaRow;
  catalogos: CatalogosInm;
  ocupado: boolean;
  onGuardar: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const [tipoOperacion, setTipoOperacion] = useState(compraventa.tipo_operacion_inm ?? "501");
  const [figuraCliente, setFiguraCliente] = useState(compraventa.figura_cliente ?? "1");
  const [figuraSo, setFiguraSo] = useState(figuraSoSoportada(compraventa.figura_so));
  const [tipoInmueble, setTipoInmueble] = useState(compraventa.tipo_inmueble ?? "1");
  const [colonia, setColonia] = useState(compraventa.inmueble_colonia ?? "");
  const [calle, setCalle] = useState(compraventa.inmueble_calle ?? "");
  const [numeroExterior, setNumeroExterior] = useState(compraventa.inmueble_numero_exterior ?? "");
  const [numeroInterior, setNumeroInterior] = useState(compraventa.inmueble_numero_interior ?? "");
  const [codigoPostal, setCodigoPostal] = useState(compraventa.inmueble_codigo_postal ?? "");
  const [terreno, setTerreno] = useState(compraventa.dimension_terreno === null ? "" : String(compraventa.dimension_terreno));
  const [construido, setConstruido] = useState(compraventa.dimension_construido === null ? "" : String(compraventa.dimension_construido));
  const [folioReal, setFolioReal] = useState(compraventa.folio_real ?? "");
  const [instrumentoTipo, setInstrumentoTipo] = useState(compraventa.instrumento_tipo ?? "contrato");
  const [fechaContrato, setFechaContrato] = useState(compraventa.fecha_contrato ?? "");
  const [numeroInstrumento, setNumeroInstrumento] = useState(compraventa.numero_instrumento_publico ?? "");
  const [fechaInstrumento, setFechaInstrumento] = useState(compraventa.fecha_instrumento_publico ?? "");
  const [notario, setNotario] = useState(compraventa.notario_instrumento_publico ?? "");
  const [entidadInstrumento, setEntidadInstrumento] = useState(compraventa.entidad_instrumento_publico?.padStart(LONGITUD_ENTIDAD_FEDERATIVA, "0") ?? "");
  const [avaluo, setAvaluo] = useState(compraventa.valor_avaluo_catastral === null ? "" : String(compraventa.valor_avaluo_catastral));
  const [guardando, setGuardando] = useState(false);
  const faltantes = faltantesDatosInm({
    colonia,
    calle,
    numeroExterior,
    codigoPostal,
    terreno,
    construido,
    folioReal,
    instrumentoTipo,
    fechaContrato,
    numeroInstrumento,
    fechaInstrumento,
    notario,
    entidadInstrumento,
    avaluo,
  });

  useEffect(() => {
    setTipoOperacion(compraventa.tipo_operacion_inm ?? "501");
    setFiguraCliente(compraventa.figura_cliente ?? "1");
    setFiguraSo(figuraSoSoportada(compraventa.figura_so));
    setTipoInmueble(compraventa.tipo_inmueble ?? "1");
    setColonia(compraventa.inmueble_colonia ?? "");
    setCalle(compraventa.inmueble_calle ?? "");
    setNumeroExterior(compraventa.inmueble_numero_exterior ?? "");
    setNumeroInterior(compraventa.inmueble_numero_interior ?? "");
    setCodigoPostal(compraventa.inmueble_codigo_postal ?? "");
    setTerreno(compraventa.dimension_terreno === null ? "" : String(compraventa.dimension_terreno));
    setConstruido(compraventa.dimension_construido === null ? "" : String(compraventa.dimension_construido));
    setFolioReal(compraventa.folio_real ?? "");
    setInstrumentoTipo(compraventa.instrumento_tipo ?? "contrato");
    setFechaContrato(compraventa.fecha_contrato ?? "");
    setNumeroInstrumento(compraventa.numero_instrumento_publico ?? "");
    setFechaInstrumento(compraventa.fecha_instrumento_publico ?? "");
    setNotario(compraventa.notario_instrumento_publico ?? "");
    setEntidadInstrumento(compraventa.entidad_instrumento_publico?.padStart(LONGITUD_ENTIDAD_FEDERATIVA, "0") ?? "");
    setAvaluo(compraventa.valor_avaluo_catastral === null ? "" : String(compraventa.valor_avaluo_catastral));
  }, [compraventa]);

  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>Datos para XML INM</p>
      {faltantes.length > 0 && (
        <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 12.5 }}>
          Faltan datos para exportar XML INM: {faltantes.join(", ")}.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={st.label}>Tipo de operación</label>
          <select style={st.input} value={tipoOperacion} onChange={(e) => { setTipoOperacion(e.target.value); }}>
            {TIPOS_OPERACION_INM.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={st.label}>Tipo de inmueble</label>
          <select style={st.input} value={tipoInmueble} onChange={(e) => { setTipoInmueble(e.target.value); }}>
            {catalogos.tiposInmueble.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={st.label}>Figura del cliente</label>
          <select style={st.input} value={figuraCliente} onChange={(e) => { setFiguraCliente(e.target.value); }}>
            {catalogos.figurasCliente.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={st.label}>Figura del sujeto obligado</label>
          <select style={st.input} value={figuraSo} onChange={(e) => { setFiguraSo(e.target.value); }}>
            {catalogos.figurasSo.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={st.label}>Colonia</label>
          <input style={st.input} value={colonia} onChange={(e) => { setColonia(e.target.value); }} />
        </div>
        <div>
          <label style={st.label}>Calle</label>
          <input style={st.input} value={calle} onChange={(e) => { setCalle(e.target.value); }} />
        </div>
        <div>
          <label style={st.label}>Número exterior</label>
          <input style={st.input} value={numeroExterior} onChange={(e) => { setNumeroExterior(e.target.value); }} />
        </div>
        <div>
          <label style={st.label}>Número interior</label>
          <input style={st.input} value={numeroInterior} onChange={(e) => { setNumeroInterior(e.target.value); }} />
        </div>
        <div>
          <label style={st.label}>Código postal</label>
          <input style={st.input} inputMode="numeric" value={codigoPostal} onChange={(e) => { setCodigoPostal(e.target.value.replace(/[^0-9]/g, "")); }} />
        </div>
        <div>
          <label style={st.label}>Folio real</label>
          <input style={st.input} maxLength={LONGITUD_FOLIO_REAL} value={folioReal} onChange={(e) => { setFolioReal(claveXml(e.target.value, LONGITUD_FOLIO_REAL)); }} />
        </div>
        <div>
          <label style={st.label}>Dimensión terreno</label>
          <input style={st.input} inputMode="decimal" value={terreno} onChange={(e) => { setTerreno(e.target.value.replace(/[^0-9.]/g, "")); }} />
        </div>
        <div>
          <label style={st.label}>Dimensión construido</label>
          <input style={st.input} inputMode="decimal" value={construido} onChange={(e) => { setConstruido(e.target.value.replace(/[^0-9.]/g, "")); }} />
        </div>
        <div>
          <label style={st.label}>Tipo de instrumento</label>
          <select style={st.input} value={instrumentoTipo} onChange={(e) => { setInstrumentoTipo(e.target.value); }}>
            <option value="contrato">Contrato</option>
            <option value="instrumento_publico">Instrumento público</option>
          </select>
        </div>
        <div>
          <label style={st.label}>Fecha de contrato</label>
          <input style={st.input} type="date" value={fechaContrato} onChange={(e) => { setFechaContrato(e.target.value); }} />
        </div>
        <div>
          <label style={st.label}>Número instrumento público</label>
          <input style={st.input} maxLength={LONGITUD_FOLIO_INSTRUMENTO} value={numeroInstrumento} onChange={(e) => { setNumeroInstrumento(claveXml(e.target.value, LONGITUD_FOLIO_INSTRUMENTO)); }} />
        </div>
        <div>
          <label style={st.label}>Fecha instrumento público</label>
          <input style={st.input} type="date" value={fechaInstrumento} onChange={(e) => { setFechaInstrumento(e.target.value); }} />
        </div>
        <div>
          <label style={st.label}>Notario</label>
          <input style={st.input} maxLength={LONGITUD_NOTARIO} value={notario} onChange={(e) => { setNotario(claveXml(e.target.value, LONGITUD_NOTARIO)); }} />
        </div>
        <div>
          <label style={st.label}>Entidad instrumento público</label>
          <select style={st.input} value={entidadInstrumento} onChange={(e) => { setEntidadInstrumento(e.target.value); }}>
            <option value="">Selecciona…</option>
            {catalogos.entidades.map(([v, t]) => <option key={v} value={v}>{v} · {t}</option>)}
          </select>
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label style={st.label}>Valor avalúo catastral</label>
          <input style={st.input} inputMode="decimal" value={avaluo} onChange={(e) => { setAvaluo(e.target.value.replace(/[^0-9.]/g, "")); }} />
        </div>
      </div>
      <button
        style={{ ...st.btn, marginTop: 10, color: C.tielDark, borderColor: C.tiel, opacity: guardando || ocupado ? OPACIDAD_DESHABILITADO : 1 }}
        disabled={guardando || ocupado}
        onClick={() => {
          setGuardando(true);
          void onGuardar({
            tipo_operacion_inm: tipoOperacion,
            figura_cliente: figuraCliente,
            figura_so: figuraSo,
            tipo_inmueble: tipoInmueble,
            inmueble_colonia: textoONull(colonia),
            inmueble_calle: textoONull(calle),
            inmueble_numero_exterior: textoONull(numeroExterior),
            inmueble_numero_interior: textoONull(numeroInterior),
            inmueble_codigo_postal: textoONull(codigoPostal),
            dimension_terreno: numeroONull(terreno),
            dimension_construido: numeroONull(construido),
            folio_real: textoONull(folioReal),
            instrumento_tipo: instrumentoTipo,
            fecha_contrato: textoONull(fechaContrato),
            numero_instrumento_publico: textoONull(numeroInstrumento),
            fecha_instrumento_publico: textoONull(fechaInstrumento),
            notario_instrumento_publico: textoONull(notario),
            entidad_instrumento_publico: entidadONull(entidadInstrumento),
            valor_avaluo_catastral: numeroONull(avaluo),
          }).finally(() => { setGuardando(false); });
        }}
      >
        {guardando ? "Guardando…" : "Guardar datos INM"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cobros de la compraventa (lista + alta inline)
// ─────────────────────────────────────────────────────────────────────────────

function CobrosCard({
  demo,
  catalogos,
  cobros,
  evals,
  hipotesis,
  onRegistrar,
}: {
  demo: boolean;
  catalogos: CatalogosInm;
  cobros: AportacionRow[] | null;
  evals: Map<string, EvaluacionMotor>;
  hipotesis: Map<string, HipotesisNormativa[]>;
  onRegistrar: (datos: Record<string, unknown>) => Promise<void>;
}) {
  const [abierto, setAbierto] = useState(false);
  const [base, setBase] = useState("");
  const [iva, setIva] = useState("");
  const [fechaCobro, setFechaCobro] = useState("");
  const [instrumento, setInstrumento] = useState("8");
  const [formaPago, setFormaPago] = useState("1");
  const [moneda, setMoneda] = useState("1");
  const [guardando, setGuardando] = useState(false);

  const nBase = Number(base) || 0;
  const nIva = Number(iva) || 0;
  const listo = nBase > 0 && fechaCobro !== "";

  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>Cobros de esta operación ({String(cobros?.length ?? 0)})</p>
      {cobros === null && <p style={{ color: C.muted, fontSize: 13 }}>Cargando cobros…</p>}
      {cobros !== null && cobros.length === 0 && (
        <p style={{ color: C.muted, fontSize: 13 }}>Sin cobros registrados todavía.</p>
      )}
      {cobros?.map((c) => {
        const v = veredictoCobro(evals.get(c.id));
        const instrumentoTxt = etiquetaCatalogo(catalogos.instrumentos, c.instrumento_monetario, c.es_efectivo ? "Efectivo" : "Instrumento");
        const monedaTxt = etiquetaCatalogo(catalogos.monedas, c.moneda, "Moneda");
        const formaPagoTxt = etiquetaCatalogo(catalogos.formasPago, c.forma_pago_inm, "Forma INM");
        return (
          <div key={c.id} style={{ padding: "8px 0", borderBottom: `1px solid ${C.line}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13.5 }}>
              <span style={{ color: C.ink }}>
                <b>{dinero(c.monto_bruto)}</b>
                <span style={{ color: C.muted }}> · {instrumentoTxt} · {monedaTxt} · {formaPagoTxt} · {fecha(c.fecha_acto)}</span>
              </span>
            </div>
            {v && (
              <div style={{ marginTop: 4, fontSize: 12, color: { verde: C.green, ambar: C.amber, rojo: C.red, gris: C.muted }[v.tono] }}>
                ● {v.texto}
              </div>
            )}
            <HipotesisInline hipotesis={hipotesis.get(c.id)} />
          </div>
        );
      })}

      {!abierto ? (
        <button style={{ ...st.btn, marginTop: 10, color: C.tielDark, borderColor: C.tiel }} onClick={() => { setAbierto(true); }}>
          ＋ Registrar cobro
        </button>
      ) : (
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={st.label}>Monto sin IVA</label>
            <input style={st.input} inputMode="numeric" placeholder="0" value={base} onChange={(e) => { setBase(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>IVA (si aplica)</label>
            <input style={st.input} inputMode="numeric" placeholder="0" value={iva} onChange={(e) => { setIva(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>Fecha del cobro efectivo</label>
            <input style={st.input} type="date" value={fechaCobro} onChange={(e) => { setFechaCobro(e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Instrumento</label>
            <select style={st.input} value={instrumento} onChange={(e) => { setInstrumento(e.target.value); }}>
              {catalogos.instrumentos.map(([v, t]) => (
                <option key={v} value={v}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={st.label}>Forma de pago INM</label>
            <select style={st.input} value={formaPago} onChange={(e) => { setFormaPago(e.target.value); }}>
              {catalogos.formasPago.map(([v, t]) => (
                <option key={v} value={v}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={st.label}>Moneda</label>
            <select style={st.input} value={moneda} onChange={(e) => { setMoneda(e.target.value); }}>
              {catalogos.monedas.map(([v, t]) => (
                <option key={v} value={v}>{t}</option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: "span 2", display: "flex", gap: 8 }}>
            <button
              style={{ ...st.btnPrim, opacity: guardando || !listo ? OPACIDAD_DESHABILITADO : 1 }}
              disabled={guardando || !listo}
              onClick={() => {
                setGuardando(true);
                void onRegistrar({
                  fecha_acto: fechaCobro,
                  monto_base: nBase,
                  monto_contribuciones: nIva,
                  monto_bruto: nBase + nIva,
                  es_efectivo: instrumento === CLAVE_EFECTIVO,
                  instrumento_monetario: instrumento,
                  forma_pago_inm: formaPago,
                  moneda,
                  tipo_aportacion: "terceros",
                  es_preventa: true,
                }).then(() => {
                  setAbierto(false);
                  setBase(""); setIva(""); setFechaCobro(""); setFormaPago("1"); setMoneda("1");
                }).catch(() => {
                  // El contenedor padre ya muestra el error; conserva la captura para reintentar.
                }).finally(() => {
                  setGuardando(false);
                });
              }}
            >
              {guardando ? "Registrando…" : demo ? "Registrar (requiere backend)" : "Registrar cobro — el motor evalúa"}
            </button>
            <button style={st.btn} onClick={() => { setAbierto(false); }}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Copropietarios (compraventa_persona, #66): personas ADICIONALES del aviso.
// ─────────────────────────────────────────────────────────────────────────────

function CopropietariosCard({
  demo,
  token,
  tenantId,
  compraventaId,
  personas,
  principalId,
}: {
  demo: boolean;
  token: string;
  tenantId: string;
  compraventaId: string;
  personas: PersonaRow[];
  principalId: string | null;
}) {
  const [rows, setRows] = useState<CopropietarioRow[] | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [personaId, setPersonaId] = useState("");
  const [pct, setPct] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (demo) { setRows([]); return; }
    setRows(await listarCopropietarios(token, compraventaId));
  }, [demo, token, compraventaId]);

  useEffect(() => {
    cargar().catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudieron cargar los copropietarios."); });
  }, [cargar]);

  const usados = new Set<string>([...(principalId ? [principalId] : []), ...(rows ?? []).map((r) => r.cliente_aportante_id)]);
  const disponibles = personas.filter((p) => !usados.has(p.id));

  const agregar = async () => {
    if (demo) { setError("En modo demostración el alta requiere backend conectado."); return; }
    if (!personaId) return;
    setOcupado(true);
    setError(null);
    try {
      await agregarCopropietario(token, tenantId, {
        compraventa_id: compraventaId,
        cliente_aportante_id: personaId,
        porcentaje_indiviso: pct.trim() === "" ? null : Number(pct),
        orden: (rows?.length ?? 0) + 1,
      });
      setPersonaId("");
      setPct("");
      setAbierto(false);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo agregar el copropietario.");
    } finally {
      setOcupado(false);
    }
  };

  const quitar = async (id: string) => {
    if (demo) return;
    setOcupado(true);
    setError(null);
    try {
      await eliminarCopropietario(token, id);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo quitar el copropietario.");
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>Copropietarios ({String(rows?.length ?? 0)})</p>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 8px" }}>
        Personas adicionales del aviso, además del comprador principal. Cada una viaja como
        persona en el XML (copropiedad — p. ej. dos cónyuges compran una unidad).
      </p>
      {error && (
        <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8, background: C.redBg, color: C.red, fontSize: 12.5 }}>{error}</div>
      )}
      {rows === null && <p style={{ color: C.muted, fontSize: 13 }}>Cargando…</p>}
      {rows !== null && rows.length === 0 && <p style={{ color: C.muted, fontSize: 13 }}>Sólo el comprador principal.</p>}
      {rows?.map((r) => {
        const p = personas.find((x) => x.id === r.cliente_aportante_id);
        return (
          <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: `1px solid ${C.line}`, fontSize: 13.5 }}>
            <span style={{ color: C.ink }}>
              {p ? nombrePersonaRow(p) : r.cliente_aportante_id}
              {r.porcentaje_indiviso != null && r.porcentaje_indiviso !== "" && (
                <span style={{ color: C.muted }}> · {String(r.porcentaje_indiviso)}% indiviso</span>
              )}
            </span>
            <button style={{ ...st.btn, padding: "4px 10px", fontSize: 12 }} disabled={ocupado} onClick={() => { void quitar(r.id); }}>Quitar</button>
          </div>
        );
      })}
      {!abierto ? (
        <button style={{ ...st.btn, marginTop: 10, color: C.tielDark, borderColor: C.tiel }} onClick={() => { setAbierto(true); }}>
          ＋ Agregar copropietario
        </button>
      ) : (
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
          <div>
            <label style={st.label}>Persona (expediente ya capturado)</label>
            <select style={st.input} value={personaId} onChange={(e) => { setPersonaId(e.target.value); }}>
              <option value="">Selecciona…</option>
              {disponibles.map((p) => <option key={p.id} value={p.id}>{nombrePersonaRow(p)}</option>)}
            </select>
          </div>
          <div>
            <label style={st.label}>% indiviso (opcional)</label>
            <input style={st.input} inputMode="decimal" value={pct} onChange={(e) => { setPct(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div style={{ gridColumn: "span 2", display: "flex", gap: 8 }}>
            <button
              style={{ ...st.btnPrim, opacity: ocupado || !personaId ? OPACIDAD_DESHABILITADO : 1 }}
              disabled={ocupado || !personaId}
              onClick={() => { void agregar(); }}
            >
              {ocupado ? "Guardando…" : "Agregar"}
            </button>
            <button style={st.btn} onClick={() => { setAbierto(false); setPersonaId(""); setPct(""); }}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inmuebles adicionales (compraventa_inmueble, #67): además del inmueble primario.
// ─────────────────────────────────────────────────────────────────────────────

function InmueblesAdicionalesCard({
  demo,
  token,
  tenantId,
  compraventaId,
  catalogos,
}: {
  demo: boolean;
  token: string;
  tenantId: string;
  compraventaId: string;
  catalogos: CatalogosInm;
}) {
  const [rows, setRows] = useState<CompraventaInmuebleRow[] | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [descripcion, setDescripcion] = useState("");
  const [tipoInmueble, setTipoInmueble] = useState("2");
  const [colonia, setColonia] = useState("");
  const [calle, setCalle] = useState("");
  const [numeroExterior, setNumeroExterior] = useState("");
  const [numeroInterior, setNumeroInterior] = useState("");
  const [codigoPostal, setCodigoPostal] = useState("");
  const [terreno, setTerreno] = useState("");
  const [construido, setConstruido] = useState("");
  const [folioReal, setFolioReal] = useState("");
  const [valor, setValor] = useState("");

  const cargar = useCallback(async () => {
    if (demo) { setRows([]); return; }
    setRows(await listarInmueblesAdicionales(token, compraventaId));
  }, [demo, token, compraventaId]);

  useEffect(() => {
    cargar().catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudieron cargar los inmuebles adicionales."); });
  }, [cargar]);

  const faltantes: string[] = [];
  if (!colonia.trim()) faltantes.push("colonia");
  if (!calle.trim()) faltantes.push("calle");
  if (!numeroExterior.trim()) faltantes.push("número exterior");
  if (!/^\d{5}$/.test(codigoPostal.trim())) faltantes.push("código postal de 5 dígitos");
  if ((Number(terreno) || 0) <= 0) faltantes.push("dimensión de terreno");
  if ((Number(construido) || 0) <= 0) faltantes.push("dimensión construida");
  const folioLimpio = folioReal.trim();
  if (!folioLimpio) faltantes.push("folio real");
  else if (!/^[A-Z0-9_-]{1,200}$/.test(folioLimpio)) faltantes.push("folio real sin espacios ni puntos");
  if ((Number(valor) || 0) <= 0) faltantes.push("valor pactado");
  const listo = faltantes.length === 0;

  const limpiar = () => {
    setDescripcion(""); setTipoInmueble("2"); setColonia(""); setCalle(""); setNumeroExterior("");
    setNumeroInterior(""); setCodigoPostal(""); setTerreno(""); setConstruido(""); setFolioReal(""); setValor("");
  };

  const agregar = async () => {
    if (demo) { setError("En modo demostración el alta requiere backend conectado."); return; }
    setOcupado(true);
    setError(null);
    try {
      await agregarInmuebleAdicional(token, tenantId, {
        compraventa_id: compraventaId,
        orden: (rows?.length ?? 0) + 1,
        descripcion: textoONull(descripcion),
        tipo_inmueble: tipoInmueble,
        inmueble_colonia: textoONull(colonia),
        inmueble_calle: textoONull(calle),
        inmueble_numero_exterior: textoONull(numeroExterior),
        inmueble_numero_interior: textoONull(numeroInterior),
        inmueble_codigo_postal: textoONull(codigoPostal),
        dimension_terreno: numeroONull(terreno),
        dimension_construido: numeroONull(construido),
        folio_real: textoONull(folioReal),
        valor_pactado: numeroONull(valor),
      });
      limpiar();
      setAbierto(false);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo agregar el inmueble.");
    } finally {
      setOcupado(false);
    }
  };

  const quitar = async (id: string) => {
    if (demo) return;
    setOcupado(true);
    setError(null);
    try {
      await eliminarInmuebleAdicional(token, id);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo quitar el inmueble.");
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>Inmuebles adicionales ({String(rows?.length ?? 0)})</p>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 8px" }}>
        Además del inmueble principal (arriba): cajón de estacionamiento, bodega u otra unidad con
        folio real distinto en la misma operación. Cada uno viaja como bloque propio en el XML, con
        su propio valor pactado.
      </p>
      {error && (
        <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8, background: C.redBg, color: C.red, fontSize: 12.5 }}>{error}</div>
      )}
      {rows === null && <p style={{ color: C.muted, fontSize: 13 }}>Cargando…</p>}
      {rows !== null && rows.length === 0 && <p style={{ color: C.muted, fontSize: 13 }}>Sólo el inmueble principal.</p>}
      {rows?.map((r) => (
        <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: `1px solid ${C.line}`, fontSize: 13.5 }}>
          <span style={{ color: C.ink }}>
            <b>{etiquetaCatalogo(catalogos.tiposInmueble, r.tipo_inmueble, "Inmueble")}</b>
            <span style={{ color: C.muted }}> · {r.folio_real} · {dinero(r.valor_pactado)}{r.descripcion ? ` · ${r.descripcion}` : ""}</span>
          </span>
          <button style={{ ...st.btn, padding: "4px 10px", fontSize: 12 }} disabled={ocupado} onClick={() => { void quitar(r.id); }}>Quitar</button>
        </div>
      ))}
      {!abierto ? (
        <button style={{ ...st.btn, marginTop: 10, color: C.tielDark, borderColor: C.tiel }} onClick={() => { setAbierto(true); }}>
          ＋ Agregar inmueble
        </button>
      ) : (
        <div style={{ marginTop: 10 }}>
          {faltantes.length > 0 && (
            <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 12.5 }}>
              Faltan datos para el inmueble: {faltantes.join(", ")}.
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={st.label}>Tipo de inmueble</label>
              <select style={st.input} value={tipoInmueble} onChange={(e) => { setTipoInmueble(e.target.value); }}>
                {catalogos.tiposInmueble.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={st.label}>Descripción (opcional)</label>
              <input style={st.input} placeholder="Ej. Cajón 12" value={descripcion} onChange={(e) => { setDescripcion(e.target.value); }} />
            </div>
            <div>
              <label style={st.label}>Colonia</label>
              <input style={st.input} value={colonia} onChange={(e) => { setColonia(e.target.value); }} />
            </div>
            <div>
              <label style={st.label}>Calle</label>
              <input style={st.input} value={calle} onChange={(e) => { setCalle(e.target.value); }} />
            </div>
            <div>
              <label style={st.label}>Número exterior</label>
              <input style={st.input} value={numeroExterior} onChange={(e) => { setNumeroExterior(e.target.value); }} />
            </div>
            <div>
              <label style={st.label}>Número interior</label>
              <input style={st.input} value={numeroInterior} onChange={(e) => { setNumeroInterior(e.target.value); }} />
            </div>
            <div>
              <label style={st.label}>Código postal</label>
              <input style={st.input} inputMode="numeric" value={codigoPostal} onChange={(e) => { setCodigoPostal(e.target.value.replace(/[^0-9]/g, "")); }} />
            </div>
            <div>
              <label style={st.label}>Folio real</label>
              <input style={st.input} maxLength={LONGITUD_FOLIO_REAL} value={folioReal} onChange={(e) => { setFolioReal(claveXml(e.target.value, LONGITUD_FOLIO_REAL)); }} />
            </div>
            <div>
              <label style={st.label}>Dimensión terreno</label>
              <input style={st.input} inputMode="decimal" value={terreno} onChange={(e) => { setTerreno(e.target.value.replace(/[^0-9.]/g, "")); }} />
            </div>
            <div>
              <label style={st.label}>Dimensión construido</label>
              <input style={st.input} inputMode="decimal" value={construido} onChange={(e) => { setConstruido(e.target.value.replace(/[^0-9.]/g, "")); }} />
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={st.label}>Valor pactado de este inmueble</label>
              <input style={st.input} inputMode="decimal" value={valor} onChange={(e) => { setValor(e.target.value.replace(/[^0-9.]/g, "")); }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              style={{ ...st.btnPrim, opacity: ocupado || !listo ? OPACIDAD_DESHABILITADO : 1 }}
              disabled={ocupado || !listo}
              onClick={() => { void agregar(); }}
            >
              {ocupado ? "Guardando…" : "Agregar inmueble"}
            </button>
            <button style={st.btn} onClick={() => { setAbierto(false); limpiar(); }}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Contraparte del intermediario (compraventa_contraparte, #68): figura_so=3.
// ─────────────────────────────────────────────────────────────────────────────

function ContraparteCard({
  demo,
  token,
  tenantId,
  compraventaId,
  personas,
  figuraSo,
}: {
  demo: boolean;
  token: string;
  tenantId: string;
  compraventaId: string;
  personas: PersonaRow[];
  figuraSo: string | null;
}) {
  const [rows, setRows] = useState<ContraparteRow[] | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [personaId, setPersonaId] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const esIntermediario = figuraSo === "3";

  const cargar = useCallback(async () => {
    if (demo) { setRows([]); return; }
    setRows(await listarContrapartes(token, compraventaId));
  }, [demo, token, compraventaId]);

  useEffect(() => {
    cargar().catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudieron cargar las contrapartes."); });
  }, [cargar]);

  const usados = new Set((rows ?? []).map((r) => r.cliente_aportante_id));
  const disponibles = personas.filter((p) => !usados.has(p.id));

  const agregar = async () => {
    if (demo) { setError("En modo demostración el alta requiere backend conectado."); return; }
    if (!personaId) return;
    setOcupado(true);
    setError(null);
    try {
      await agregarContraparte(token, tenantId, {
        compraventa_id: compraventaId,
        cliente_aportante_id: personaId,
        orden: (rows?.length ?? 0) + 1,
      });
      setPersonaId("");
      setAbierto(false);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo agregar la contraparte.");
    } finally {
      setOcupado(false);
    }
  };

  const quitar = async (id: string) => {
    if (demo) return;
    setOcupado(true);
    setError(null);
    try {
      await eliminarContraparte(token, id);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo quitar la contraparte.");
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>Contraparte del intermediario ({String(rows?.length ?? 0)})</p>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 8px" }}>
        Aplica cuando el sujeto obligado actúa como Intermediario (figura del SO = Intermediario).
        Cada contraparte viaja como bloque datos_contraparte en el XML.
      </p>
      {esIntermediario && (rows?.length ?? 0) === 0 && (
        <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 12.5 }}>
          Como intermediario (figura del SO = 3), el XML exige al menos una contraparte. Sin ella la
          generación del XML fallará.
        </div>
      )}
      {error && (
        <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8, background: C.redBg, color: C.red, fontSize: 12.5 }}>{error}</div>
      )}
      {rows === null && <p style={{ color: C.muted, fontSize: 13 }}>Cargando…</p>}
      {rows !== null && rows.length === 0 && !esIntermediario && (
        <p style={{ color: C.muted, fontSize: 13 }}>Sin contrapartes (sólo para intermediario).</p>
      )}
      {rows?.map((r) => {
        const p = personas.find((x) => x.id === r.cliente_aportante_id);
        return (
          <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: `1px solid ${C.line}`, fontSize: 13.5 }}>
            <span style={{ color: C.ink }}>{p ? nombrePersonaRow(p) : r.cliente_aportante_id}</span>
            <button style={{ ...st.btn, padding: "4px 10px", fontSize: 12 }} disabled={ocupado} onClick={() => { void quitar(r.id); }}>Quitar</button>
          </div>
        );
      })}
      {!abierto ? (
        <button style={{ ...st.btn, marginTop: 10, color: C.tielDark, borderColor: C.tiel }} onClick={() => { setAbierto(true); }}>
          ＋ Agregar contraparte
        </button>
      ) : (
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
          <div>
            <label style={st.label}>Persona contraparte (expediente ya capturado)</label>
            <select style={st.input} value={personaId} onChange={(e) => { setPersonaId(e.target.value); }}>
              <option value="">Selecciona…</option>
              {disponibles.map((p) => <option key={p.id} value={p.id}>{nombrePersonaRow(p)}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              style={{ ...st.btnPrim, opacity: ocupado || !personaId ? OPACIDAD_DESHABILITADO : 1 }}
              disabled={ocupado || !personaId}
              onClick={() => { void agregar(); }}
            >
              {ocupado ? "Guardando…" : "Agregar"}
            </button>
            <button style={st.btn} onClick={() => { setAbierto(false); setPersonaId(""); }}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Alta de compraventa
// ─────────────────────────────────────────────────────────────────────────────

function FormCompraventa({
  demo,
  catalogos,
  personas,
  proyectos,
  onCrear,
  onCancelar,
}: {
  demo: boolean;
  catalogos: CatalogosInm;
  personas: PersonaRow[];
  proyectos: ProyectoMin[];
  onCrear: (datos: Record<string, unknown>) => Promise<void>;
  onCancelar: () => void;
}) {
  const [unidad, setUnidad] = useState("");
  const [personaId, setPersonaId] = useState("");
  const [proyectoId, setProyectoId] = useState("");
  const [precio, setPrecio] = useState("");
  const [etapa, setEtapa] = useState("apartado");
  const [fechaOperacion, setFechaOperacion] = useState("");
  const [tipoOperacion, setTipoOperacion] = useState("501");
  const [figuraCliente, setFiguraCliente] = useState("1");
  const [figuraSo, setFiguraSo] = useState("2");
  const [tipoInmueble, setTipoInmueble] = useState("1");
  const [colonia, setColonia] = useState("");
  const [calle, setCalle] = useState("");
  const [numeroExterior, setNumeroExterior] = useState("");
  const [numeroInterior, setNumeroInterior] = useState("");
  const [codigoPostal, setCodigoPostal] = useState("");
  const [terreno, setTerreno] = useState("");
  const [construido, setConstruido] = useState("");
  const [folioReal, setFolioReal] = useState("");
  const [instrumentoTipo, setInstrumentoTipo] = useState("contrato");
  const [fechaContrato, setFechaContrato] = useState("");
  const [numeroInstrumento, setNumeroInstrumento] = useState("");
  const [fechaInstrumento, setFechaInstrumento] = useState("");
  const [notario, setNotario] = useState("");
  const [entidadInstrumento, setEntidadInstrumento] = useState("");
  const [avaluo, setAvaluo] = useState("");
  const [guardando, setGuardando] = useState(false);

  const persona = personas.find((p) => p.id === personaId);
  const faltantesOperacion: string[] = [];
  if (!unidad.trim()) faltantesOperacion.push("unidad");
  if (!personaId) faltantesOperacion.push("comprador");
  if (!proyectoId) faltantesOperacion.push("proyecto");
  if ((Number(precio) || 0) <= 0) faltantesOperacion.push("precio pactado");
  if (!fechaOperacion) faltantesOperacion.push("fecha de operación");
  const faltantes = faltantesDatosInm({
    colonia,
    calle,
    numeroExterior,
    codigoPostal,
    terreno,
    construido,
    folioReal,
    instrumentoTipo,
    fechaContrato,
    numeroInstrumento,
    fechaInstrumento,
    notario,
    entidadInstrumento,
    avaluo,
  });
  const faltantesRegistro = [...faltantesOperacion, ...faltantes];
  const listo = faltantesRegistro.length === 0;

  const nombreDe = (p: PersonaRow): string => {
    if (p.tipo === "pm") return p.denominacion_razon ?? "(sin razón social)";
    if (p.tipo === "fideicomiso") return p.fideicomiso_identificador ? `Fideicomiso ${p.fideicomiso_identificador}` : "(fideicomiso)";
    return [p.nombre, p.apellido_paterno, p.apellido_materno].filter((x): x is string => !!x).join(" ") || "(sin nombre)";
  };

  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      <button onClick={onCancelar} style={{ border: "none", background: "transparent", color: C.muted, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 8 }}>
        ‹ Cancelar
      </button>
      <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>Registrar compraventa</h2>
      <p style={{ margin: "0 0 14px", color: C.muted, fontSize: 13 }}>
        La venta de una unidad: apartado, contrato o escritura. Los cobros se registran
        después, dentro de la misma operación, y cada uno lo evalúa el motor.
      </p>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Operación</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Unidad</label>
            <input style={st.input} placeholder="Ej. Depto 402 · Torre Norte" value={unidad} onChange={(e) => { setUnidad(e.target.value); }} />
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Comprador (expediente ya capturado)</label>
            <select style={st.input} value={personaId} onChange={(e) => { setPersonaId(e.target.value); }}>
              <option value="">Selecciona…</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>{nombreDe(p)}</option>
              ))}
            </select>
            {persona && !persona.expediente_completo && (
              <div style={{ marginTop: 6, padding: "7px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 12 }}>
                El expediente del comprador está incompleto: puedes apartar, pero cumplimiento
                deberá completarlo antes de avanzar la operación.
              </div>
            )}
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Proyecto</label>
            <select style={st.input} value={proyectoId} onChange={(e) => { setProyectoId(e.target.value); }}>
              <option value="">Selecciona…</option>
              {proyectos.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={st.label}>Precio pactado (sin IVA)</label>
            <input style={st.input} inputMode="numeric" placeholder="0" value={precio} onChange={(e) => { setPrecio(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>Fecha</label>
            <input style={st.input} type="date" value={fechaOperacion} onChange={(e) => { setFechaOperacion(e.target.value); }} />
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Etapa</label>
            <div style={{ display: "flex", gap: 8 }}>
              {ETAPAS.map(([v, t]) => (
                <button
                  key={v}
                  onClick={() => { setEtapa(v); }}
                  style={{
                    ...st.btn,
                    borderColor: etapa === v ? C.tiel : C.line,
                    background: etapa === v ? C.tielSoft : "#fff",
                    color: etapa === v ? C.tielDark : C.ink,
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Datos para XML INM</p>
        {faltantesRegistro.length > 0 && (
          <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 12.5 }}>
            Faltan datos para registrar y exportar XML INM: {faltantesRegistro.join(", ")}.
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={st.label}>Tipo de operación</label>
            <select style={st.input} value={tipoOperacion} onChange={(e) => { setTipoOperacion(e.target.value); }}>
              {TIPOS_OPERACION_INM.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={st.label}>Tipo de inmueble</label>
            <select style={st.input} value={tipoInmueble} onChange={(e) => { setTipoInmueble(e.target.value); }}>
              {catalogos.tiposInmueble.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={st.label}>Figura del cliente</label>
            <select style={st.input} value={figuraCliente} onChange={(e) => { setFiguraCliente(e.target.value); }}>
              {catalogos.figurasCliente.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={st.label}>Figura del sujeto obligado</label>
            <select style={st.input} value={figuraSo} onChange={(e) => { setFiguraSo(e.target.value); }}>
              {catalogos.figurasSo.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={st.label}>Colonia</label>
            <input style={st.input} value={colonia} onChange={(e) => { setColonia(e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Calle</label>
            <input style={st.input} value={calle} onChange={(e) => { setCalle(e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Número exterior</label>
            <input style={st.input} value={numeroExterior} onChange={(e) => { setNumeroExterior(e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Número interior</label>
            <input style={st.input} value={numeroInterior} onChange={(e) => { setNumeroInterior(e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Código postal</label>
            <input style={st.input} inputMode="numeric" value={codigoPostal} onChange={(e) => { setCodigoPostal(e.target.value.replace(/[^0-9]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>Folio real</label>
            <input style={st.input} maxLength={LONGITUD_FOLIO_REAL} value={folioReal} onChange={(e) => { setFolioReal(claveXml(e.target.value, LONGITUD_FOLIO_REAL)); }} />
          </div>
          <div>
            <label style={st.label}>Dimensión terreno</label>
            <input style={st.input} inputMode="decimal" value={terreno} onChange={(e) => { setTerreno(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>Dimensión construido</label>
            <input style={st.input} inputMode="decimal" value={construido} onChange={(e) => { setConstruido(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>Tipo de instrumento</label>
            <select style={st.input} value={instrumentoTipo} onChange={(e) => { setInstrumentoTipo(e.target.value); }}>
              <option value="contrato">Contrato</option>
              <option value="instrumento_publico">Instrumento público</option>
            </select>
          </div>
          <div>
            <label style={st.label}>Fecha de contrato</label>
            <input style={st.input} type="date" value={fechaContrato} onChange={(e) => { setFechaContrato(e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Número instrumento público</label>
            <input style={st.input} maxLength={LONGITUD_FOLIO_INSTRUMENTO} value={numeroInstrumento} onChange={(e) => { setNumeroInstrumento(claveXml(e.target.value, LONGITUD_FOLIO_INSTRUMENTO)); }} />
          </div>
          <div>
            <label style={st.label}>Fecha instrumento público</label>
            <input style={st.input} type="date" value={fechaInstrumento} onChange={(e) => { setFechaInstrumento(e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Notario</label>
            <input style={st.input} maxLength={LONGITUD_NOTARIO} value={notario} onChange={(e) => { setNotario(claveXml(e.target.value, LONGITUD_NOTARIO)); }} />
          </div>
          <div>
            <label style={st.label}>Entidad instrumento público</label>
            <select style={st.input} value={entidadInstrumento} onChange={(e) => { setEntidadInstrumento(e.target.value); }}>
              <option value="">Selecciona…</option>
              {catalogos.entidades.map(([v, t]) => <option key={v} value={v}>{v} · {t}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Valor avalúo catastral</label>
            <input style={st.input} inputMode="decimal" value={avaluo} onChange={(e) => { setAvaluo(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
        </div>
      </div>

      <button
        style={{ ...st.btnPrim, width: "100%", opacity: guardando || !listo ? OPACIDAD_DESHABILITADO : 1 }}
        disabled={guardando || !listo}
        onClick={() => {
          setGuardando(true);
          void onCrear({
            unidad: unidad.trim(),
            cliente_aportante_id: personaId,
            proyecto_id: proyectoId,
            precio_pactado: Number(precio),
            etapa,
            fecha_operacion: fechaOperacion,
            tipo_operacion_inm: tipoOperacion,
            figura_cliente: figuraCliente,
            figura_so: figuraSo,
            tipo_inmueble: tipoInmueble,
            inmueble_colonia: textoONull(colonia),
            inmueble_calle: textoONull(calle),
            inmueble_numero_exterior: textoONull(numeroExterior),
            inmueble_numero_interior: textoONull(numeroInterior),
            inmueble_codigo_postal: textoONull(codigoPostal),
            dimension_terreno: numeroONull(terreno),
            dimension_construido: numeroONull(construido),
            folio_real: textoONull(folioReal),
            instrumento_tipo: instrumentoTipo,
            fecha_contrato: textoONull(fechaContrato),
            numero_instrumento_publico: textoONull(numeroInstrumento),
            fecha_instrumento_publico: textoONull(fechaInstrumento),
            notario_instrumento_publico: textoONull(notario),
            entidad_instrumento_publico: entidadONull(entidadInstrumento),
            valor_avaluo_catastral: numeroONull(avaluo),
          }).finally(() => { setGuardando(false); });
        }}
      >
        {guardando ? "Guardando…" : demo ? "Registrar (requiere backend)" : "Registrar compraventa"}
      </button>
      <p style={{ fontSize: 12, color: C.muted, textAlign: "center", margin: "10px 0 24px" }}>
        Los cobros se capturan en el detalle; cada uno vigila el límite de efectivo con IVA.
      </p>
    </div>
  );
}
